import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Badge,
    Filter,
    GraduationCap,
    User as UserIcon,
    Building2,
    Loader2,
    ArrowLeft,
} from 'lucide-react';
import { API_CONFIG } from '../config/api';
import type { FilterOptionsResponse } from '../config/api';
import { PSGCLocation, getRegions, getProvinces } from '../utils/locationApi';
import { useTheme } from '../contexts/ThemeContext';
import '../styles/pages/EnterpriseGroupCreationPage.scss';
import SelectDropdown from '../components/SelectDropdown';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
});

// Helper: fetch /api/users/admin/filter-options with no location params (just category)
async function fetchDemographicOptions(category: string): Promise<FilterOptionsResponse | null> {
    try {
        const qs = new URLSearchParams({ category });
        const res = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_FILTER_OPTIONS}?${qs}`, {
            headers: getAuthHeaders(),
        });
        if (!res.ok) return null;
        const json = await res.json();
        return json.success ? (json.data as FilterOptionsResponse) : null;
    } catch {
        return null;
    }
}

const EMPTY_OPTIONS: FilterOptionsResponse = {
    regions: [], provinces: [], cities: [], barangays: [],
    ageRanges: [], courses: [], yearLevels: [], schools: [],
};


const EnterpriseGroupCreationPage: React.FC = () => {
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();

    // ── Group Identity ───────────────────────────────────────────────
    const [groupName, setGroupName] = useState('');
    const [description, setDescription] = useState('');

    // ── Category ─────────────────────────────────────────────────────
    const [activeCategory, setActiveCategory] = useState<'student' | 'general' | 'company'>('student');

    // ── Filter selections ────────────────────────────────────────────
    const [region, setRegion] = useState('all');
    const [province, setProvince] = useState('all');
    const [city, setCity] = useState('all');
    const [barangay, setBarangay] = useState('all');
    const [ageRange, setAgeRange] = useState('all');
    const [school, setSchool] = useState('all');
    const [educationLevel, setEducationLevel] = useState('all');
    const [course, setCourse] = useState('all');

    // ── Filter options displayed in dropdowns ────────────────────────
    const [filterOptions, setFilterOptions] = useState<FilterOptionsResponse>(EMPTY_OPTIONS);
    const [filterLoading, setFilterLoading] = useState(false);

    // ── Cached PSGC data for instant location cascading ──────────────
    const [psgcRegions, setPsgcRegions] = useState<PSGCLocation[]>([]);
    const [psgcProvinces, setPsgcProvinces] = useState<PSGCLocation[]>([]);
    const [psgcCities, setPsgcCities] = useState<PSGCLocation[]>([]);
    const [psgcBarangays, setPsgcBarangays] = useState<PSGCLocation[]>([]);

    // ── Submission ───────────────────────────────────────────────────
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ── Load PSGC data on mount (only once) ──────────────────────────
    useEffect(() => {
        const loadPsgcData = async () => {
            try {
                const regions = await getRegions();
                const provinces = await getProvinces();
                
                // Fetch all cities from API
                const citiesResponse = await fetch('https://psgc.gitlab.io/api/cities-municipalities.json');
                const cities = citiesResponse.ok ? await citiesResponse.json() : [];
                
                // Fetch all barangays - we'll cache on-demand
                const barangaysResponse = await fetch('https://psgc.gitlab.io/api/barangays.json');
                const barangays = barangaysResponse.ok ? await barangaysResponse.json() : [];
                
                setPsgcRegions(regions || []);
                setPsgcProvinces(provinces || []);
                setPsgcCities(cities || []);
                setPsgcBarangays(barangays || []);
            } catch (err) {
                console.error('Failed to load PSGC data:', err);
            }
        };
        loadPsgcData();
    }, []);

    // ── Load demographic options when category changes ────────────────
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setFilterLoading(true);
            const data = await fetchDemographicOptions(activeCategory);
            if (!cancelled && data) setFilterOptions(data);
            if (!cancelled) setFilterLoading(false);
        };
        load();
        return () => { cancelled = true; };
    }, [activeCategory]);

    // ── Update displayed provinces when region changes ────────────────
    useEffect(() => {
        let displayProvinces: string[] = [];
        if (region !== 'all' && psgcRegions.length > 0) {
            const regionObj = psgcRegions.find(r => {
                const displayName = r.regionName && r.name && r.regionName !== r.name 
                    ? `${r.regionName} (${r.name})` 
                    : (r.regionName || r.name);
                return displayName === region;
            });
            if (regionObj) {
                displayProvinces = psgcProvinces
                    .filter(p => p.regionCode === regionObj.code)
                    .map(p => p.name)
                    .filter(n => n)
                    .sort();
            }
            setProvince('all');
            setCity('all');
            setBarangay('all');
        } else {
            setProvince('all');
            setCity('all');
            setBarangay('all');
        }
        setFilterOptions(prev => ({ ...prev, provinces: displayProvinces, cities: [], barangays: [] }));
    }, [region, psgcRegions, psgcProvinces]);

    // ── Update displayed cities when province changes ────────────────
    useEffect(() => {
        let displayCities: string[] = [];
        if (province !== 'all' && region !== 'all' && psgcProvinces.length > 0) {
            const provinceObj = psgcProvinces.find(p => p.name === province);
            if (provinceObj) {
                displayCities = psgcCities
                    .filter(c => c.provinceCode === provinceObj.code)
                    .map(c => c.name)
                    .filter(n => n)
                    .sort();
            }
        } else if (region !== 'all' && filterOptions.provinces.length === 0) {
            // For regions without provinces, show cities from that region
            const regionObj = psgcRegions.find(r => {
                const displayName = r.regionName && r.name && r.regionName !== r.name 
                    ? `${r.regionName} (${r.name})` 
                    : (r.regionName || r.name);
                return displayName === region;
            });
            if (regionObj) {
                displayCities = psgcCities
                    .filter(c => c.regionCode === regionObj.code)
                    .map(c => c.name)
                    .filter(n => n)
                    .sort();
            }
        }
        setCity('all');
        setBarangay('all');
        setFilterOptions(prev => ({ ...prev, cities: displayCities, barangays: [] }));
    }, [province, region, psgcCities, psgcProvinces, psgcRegions, filterOptions.provinces.length]);

    // ── Update displayed barangays when city changes ────────────────
    useEffect(() => {
        let displayBarangays: string[] = [];
        if (city !== 'all' && psgcCities.length > 0) {
            const cityObj = psgcCities.find(c => c.name === city);
            if (cityObj) {
                displayBarangays = psgcBarangays
                    .filter(b => b.cityCode === cityObj.code)
                    .map(b => b.name)
                    .filter(n => n)
                    .sort();
            }
        }
        setBarangay('all');
        setFilterOptions(prev => ({ ...prev, barangays: displayBarangays }));
    }, [city, psgcCities, psgcBarangays]);

    // ── Create group ─────────────────────────────────────────────────
    const handleCreateGroup = async () => {
        if (!groupName.trim()) { toast.error('Group name is required.'); return; }
        if (!description.trim()) { toast.error('Description is required.'); return; }

        setIsSubmitting(true);
        try {
            // Prepare request body with city, region, and barangay
            const requestBody: Record<string, any> = {
                groupName: groupName.trim(),
                description: description.trim(),
                consentAgreed: true,
                groupCategory: activeCategory
            };

            // Only include city if not 'all'
            if (city !== 'all') {
                requestBody.city = city;
            }

            // Only include region if not 'all'
            if (region !== 'all') {
                requestBody.region = region;
            }

            // Only include barangay if not 'all'
            if (barangay !== 'all') {
                requestBody.barangay = barangay;
            }

            // Only include ageRange if not 'all'
            if (ageRange !== 'all') {
                requestBody.ageRange = ageRange;
            }

            // Student-specific filters
            if (activeCategory === 'student') {
                if (school !== 'all') requestBody.school = school;
                if (educationLevel !== 'all') requestBody.educationLevel = educationLevel;
                if (educationLevel === 'college' && course !== 'all') requestBody.course = course;
            }

            const res = await fetch(`${API_BASE}/api/groups/enterprise`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(requestBody),
            });
            const json = await res.json();
            console.log('📦 [handleSubmit] Group creation response:', {
                success: json.success,
                groupName: json.data?.groupName,
                groupId: json.data?._id,
                folderId: json.data?.folderId,
                archived: json.data?.archived,
                city: json.data?.demographicFilters?.city
            });
            console.log('📦 [handleSubmit] Full response data:', json.data);
            if (json.success) {
                toast.success(`Group "${json.data.groupName}" created successfully!`);
                // Clear the enterprise groups cache to ensure the new group appears
                sessionStorage.removeItem('enterprise_group_counts_v1');
                // Redirect to the folder containing the group so it's immediately visible
                const groupFolderId = json.data.folderId;
                console.log('📝 [handleSubmit] Extracted folderId:', groupFolderId, 'Type:', typeof groupFolderId);
                const redirectPath = groupFolderId ? `/panel/enterprise/groups/${groupFolderId}` : '/panel/enterprise/groups';
                console.log('📍 [handleSubmit] Redirecting to:', redirectPath, 'in 2 seconds');
                setTimeout(() => {
                    console.log('📍 [handleSubmit] Executing navigation to:', redirectPath);
                    navigate(redirectPath);
                }, 2000);
            } else {
                toast.error(json.message || 'Failed to create group.');
            }
        } catch {
            toast.error('Network error while creating group.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Dropdown change handlers ──────────────────────────────────────
    const handleProvinceChange = (val: string) => {
        setProvince(val);
        setCity('all'); setBarangay('all'); setSchool('all');
    };

    const handleCityChange = (val: string) => {
        setCity(val);
        setBarangay('all'); setSchool('all');
    };

    // ── Render ────────────────────────────────────────────────────────
    return (
        <div className="enterprise-group-creation">
            {/* ── Header ── */}
            <div className="enterprise-group-creation__top-bar">
                <button className="btn-back" onClick={() => navigate('/panel/enterprise/groups')}>
                    <ArrowLeft size={20} />
                </button>
                <div className="enterprise-group-creation__title">
                    <h1>Create New Group</h1>
                    <p>Define group criteria and visibility settings</p>
                </div>
            </div>

            {/* ── Main Content ── */}
            <div className="enterprise-group-creation__content">
                
                {/* ── Group Identity ── */}
                <section className="enterprise-group-creation__section">
                    <div className="enterprise-group-creation__section-header">
                        <div className="icon-badge">
                            <Badge size={20} />
                        </div>
                        <div>
                            <h2>Group Identity</h2>
                            <p>Set the name and description for your group</p>
                        </div>
                    </div>
                    <div className="enterprise-group-creation__form-grid">
                        <div className="enterprise-group-creation__input-group">
                            <label>Group Name *</label>
                            <input type="text" placeholder="e.g. Q4 Regional Operations" value={groupName} onChange={e => setGroupName(e.target.value)} />
                        </div>
                        <div className="enterprise-group-creation__input-group">
                            <label>Description *</label>
                            <input type="text" placeholder="Specify group scope and objectives" value={description} onChange={e => setDescription(e.target.value)} />
                        </div>
                    </div>
                </section>

                {/* ── Visibility Filters ── */}
                <section className="enterprise-group-creation__section">
                    <div className="enterprise-group-creation__section-header">
                        <div className="icon-badge">
                            <Filter size={20} />
                        </div>
                        <div>
                            <h2>Type of Group</h2>
                            <p>Select which group type would you like to create</p>
                        </div>
                    </div>
                    
                    <div className="enterprise-group-creation__filters-container">
                        {/* Category select */}
                        <div className="enterprise-group-creation__category-select">
                            <label>Select Category</label>
                            <div className="category-grid">
                                {(['student', 'general', 'company'] as const).map(cat => (
                                    <div key={cat} className={`category-card ${activeCategory === cat ? 'category-card--active' : ''}`}
                                        onClick={() => setActiveCategory(cat)}>
                                        <div className="icon-container">
                                            {cat === 'student' ? <GraduationCap className="icon" /> : cat === 'company' ? <Building2 className="icon" /> : <UserIcon className="icon" />}
                                        </div>
                                        <div className="details">
                                            <h3>{cat === 'student' ? 'School/University' : cat === 'company' ? 'Company' : 'General'}</h3>
                                            <p>{cat === 'student' ? 'Filter by academic data' : cat === 'company' ? 'Filter by company data' : 'Filter by demographic data'}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Dropdown Filters */}
                        {filterLoading ? (
                            <div className="enterprise-group-creation__loading">
                                <Loader2 size={20} className="icon spin" /> 
                                <span>Loading filter options…</span>
                            </div>
                        ) : (
                            <>
                                {!filterOptions.regions?.length && (
                                    <div className="enterprise-group-creation__info-note">
                                        <strong>ℹ️ Note:</strong> Users match groups based on their demographic data. Set visibility criteria below.
                                    </div>
                                )}
                                <div className="enterprise-group-creation__dropdown-grid">

                                    {/* Education Level (student only) — first */}
                                    {activeCategory === 'student' && (
                                        <div className="enterprise-group-creation__filter-item">
                                            <label>Education Level</label>
                                            <SelectDropdown
                                                value={educationLevel}
                                                onChange={(val) => {
                                                    setEducationLevel(val);
                                                    if (val !== 'college') setCourse('all');
                                                }}
                                                options={[
                                                    { value: 'all', label: 'All education levels' },
                                                    { value: 'preschool_to_highschool', label: 'Preschool to Highschool' },
                                                    { value: 'college', label: 'College' },
                                                ]}
                                                placeholder="Select education level"
                                            />
                                        </div>
                                    )}

                                    {/* Region */}
                                    <div className="enterprise-group-creation__filter-item">
                                        <label>Region {filterOptions.regions.length > 0 && <span className="count-badge">({filterOptions.regions.length})</span>}</label>
                                        <SelectDropdown
                                            value={region}
                                            onChange={setRegion}
                                            options={[
                                                { value: 'all', label: 'All regions' },
                                                ...filterOptions.regions.map(r => ({ value: r, label: r }))
                                            ]}
                                            placeholder="Select region"
                                        />
                                    </div>

                                    {/* Province - only show if region has provinces */}
                                    {filterOptions.provinces.length > 0 && (
                                    <div className="enterprise-group-creation__filter-item">
                                        <label>Province {filterOptions.provinces.length > 0 && <span className="count-badge">({filterOptions.provinces.length})</span>}</label>
                                        <SelectDropdown
                                            value={province}
                                            onChange={handleProvinceChange}
                                            options={[
                                                { value: 'all', label: 'All provinces' },
                                                ...filterOptions.provinces.map(p => ({ value: p, label: p }))
                                            ]}
                                            placeholder="Select province"
                                        />
                                    </div>
                                    )}

                                    {/* Age Range — hidden for School/University */}
                                    {activeCategory !== 'student' && (
                                    <div className="enterprise-group-creation__filter-item">
                                        <label>Age Range</label>
                                        <SelectDropdown
                                            value={ageRange}
                                            onChange={setAgeRange}
                                            options={[
                                                { value: 'all', label: 'All ages' },
                                                ...filterOptions.ageRanges.map(a => ({ value: a, label: a }))
                                            ]}
                                            placeholder="Select age range"
                                        />
                                    </div>
                                    )}

                                    {/* School (student only) */}
                                    {activeCategory === 'student' && (
                                        <div className="enterprise-group-creation__filter-item">
                                            <label>School {filterOptions.schools.length > 0 && <span className="count-badge">({filterOptions.schools.length})</span>}</label>
                                            <SelectDropdown
                                                value={school}
                                                onChange={setSchool}
                                                options={[
                                                    { value: 'all', label: 'All schools' },
                                                    ...filterOptions.schools.map(s => ({ value: s, label: s }))
                                                ]}
                                                placeholder="Select school"
                                            />
                                        </div>
                                    )}

                                    {/* City */}
                                    <div className="enterprise-group-creation__filter-item">
                                        <label>City {filterOptions.cities.length > 0 && <span className="count-badge">({filterOptions.cities.length})</span>}</label>
                                        <SelectDropdown
                                            value={city}
                                            onChange={handleCityChange}
                                            options={[
                                                { value: 'all', label: 'All cities' },
                                                ...filterOptions.cities.map(c => ({ value: c, label: c }))
                                            ]}
                                            placeholder="Select city"
                                        />
                                    </div>

                                    {/* Barangay */}
                                    <div className="enterprise-group-creation__filter-item">
                                        <label>Barangay {filterOptions.barangays.length > 0 && <span className="count-badge">({filterOptions.barangays.length})</span>}</label>
                                        <SelectDropdown
                                            value={barangay}
                                            onChange={setBarangay}
                                            options={[
                                                { value: 'all', label: 'All barangays' },
                                                ...filterOptions.barangays.map(b => ({ value: b, label: b }))
                                            ]}
                                            placeholder="Select barangay"
                                        />
                                    </div>

                                    {/* Course — only when College is selected */}
                                    {activeCategory === 'student' && educationLevel === 'college' && (
                                        <div className="enterprise-group-creation__filter-item">
                                            <label>Course {filterOptions.courses.length > 0 && <span className="count-badge">({filterOptions.courses.length})</span>}</label>
                                            <SelectDropdown
                                                value={course}
                                                onChange={setCourse}
                                                options={[
                                                    { value: 'all', label: 'All courses' },
                                                    ...filterOptions.courses.map(c => ({ value: c, label: c }))
                                                ]}
                                                placeholder="Select course"
                                            />
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </section>
            </div>

            {/* ── Footer ── */}
            <footer className="enterprise-group-creation__footer">
                <div className="enterprise-group-creation__footer-content">
                    <button className="btn-cancel" onClick={() => navigate('/panel/enterprise/groups')}>
                        Cancel
                    </button>
                    <button className="btn-create" onClick={handleCreateGroup} disabled={isSubmitting}>
                        {isSubmitting ? <><Loader2 size={18} className="icon spin" /> Creating…</> : <>Create Group</>}
                    </button>
                </div>
            </footer>
        </div>
    );
};

export default EnterpriseGroupCreationPage;
